import React from 'react';
import { Campo } from '../../../services/tipos';
import { TipoFormulario } from '../../../services/constantes';
import MapaCercaPicker from '../components/mapa-cerca-picker';
import ExplicacaoInatividade from '../components/explicacao-inatividade';
import KmMediaDiaInput from '../components/km-media-dia-input';
import HorarioPernoiteInput from '../components/horario-pernoite-input';
import ExplicacaoPernoite from '../components/explicacao-pernoite';
import LocacoesMonitoramentoPicker from '../components/locacoes-monitoramento-picker';

// Mesma precedência/paleta da tela de veículos: Vendido > Roubado > Inativo > Em locação > Parado.
const tagStatusVeiculo = (v: any) => {
  if (v?.vendido) return { texto: 'Vendido', cor: '#9E9E9E' };
  if (v?.roubado) return { texto: 'Roubado', cor: '#212121' };
  if (v?.inativo) return { texto: 'Inativo', cor: '#90A4AE' };
  if (v?.status === 'Em locação') return { texto: 'Em locação', cor: '#4CAF50' };
  return { texto: 'Parado', cor: '#f44336' };
};

export const TIPO_INADIMPLENCIA = 1;
export const TIPO_INATIVIDADE = 2;
export const TIPO_EXCESSO_ATIVIDADE = 3;
export const TIPO_DISTANCIAMENTO = 4;
export const TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO = 5;
export const TIPO_MONITORAMENTO_ENDERECO = 6;

export const REGRA_DIAS_INATIVOS = 1;
export const REGRA_KM_BAIXO = 2;

// Config JSON default para o tipo inadimplência
export const configInadimplenciaDefault = JSON.stringify({
  diasAtrasoParaAcao: 5,
  avisosPreventivos: [],
  desbloqueioAutomaticoAoPagar: true,
});

// Tipos de alvo (espelha TipoAlvoRisco do backend)
export const ALVO_TODOS = 1;
export const ALVO_POR_VEICULO = 2;
export const ALVO_POR_LOCATARIO = 3;
export const ALVO_POR_LOCACAO = 4;

const ehPernoite = (e: any) => Number(e?.tipo) === TIPO_MONITORAMENTO_ENDERECO;

interface OpcoesCamposEvento {
  modo: TipoFormulario;
  veiculosDataSource: any[];
  locatariosDataSource: any[];
  veiculosInicial: any[];
  locatariosInicial: any[];
  onVeiculosChange: (data: any[]) => void;
  onLocatariosChange: (data: any[]) => void;
  idEmpresa: string;
  locacoesIniciais: string[];
  onLocacoesChange: (ids: string[]) => void;
}

export const camposEvento = (opts: OpcoesCamposEvento): Campo[] => [
  // ── Identificação ──────────────────────────────────────────
  {
    label: 'Nome do evento',
    nome: 'nome',
    tipo: 'text',
    required: true,
    largura: 6,
    coluna: 1,
    linha: 1,
    visible: true,
    tudoMaiusculo: false,
  },
  {
    label: 'Evento',
    nome: 'tipo',
    tipo: 'select',
    required: true,
    largura: 6,
    coluna: 2,
    linha: 1,
    visible: true,
    opcoes: [
      { label: 'Bloqueio por Inadimplência', valor: TIPO_INADIMPLENCIA },
      { label: 'Alerta de Inatividade', valor: TIPO_INATIVIDADE },
      { label: 'Alerta de Excesso de Atividade', valor: TIPO_EXCESSO_ATIVIDADE },
      { label: 'Alerta de Distanciamento', valor: TIPO_DISTANCIAMENTO },
      { label: 'Alerta e Bloqueio de Distanciamento', valor: TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO },
      { label: 'Monitoramento de Endereço (pernoite)', valor: TIPO_MONITORAMENTO_ENDERECO },
    ],
  },

  // ── Aplicação ──────────────────────────────────────────────
  {
    label: 'Aplicação',
    nome: 'secAplicacao',
    tipo: 'label',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 3,
    visible: true,
  },
  {
    label: 'Aplicar a',
    nome: 'alvoTipo',
    tipo: 'select',
    required: true,
    largura: 6,
    coluna: 1,
    linha: 4,
    visible: (e: any) => !ehPernoite(e),
    opcoes: [
      { label: 'Todos', valor: ALVO_TODOS },
      { label: 'Por veículo', valor: ALVO_POR_VEICULO },
      { label: 'Por locatário', valor: ALVO_POR_LOCATARIO },
    ],
  },
  // O Monitoramento de Endereço é sempre por locação: todas as ativas (e as novas) ou as escolhidas.
  {
    label: 'Aplicar a',
    nome: 'alvoTipo',
    tipo: 'select',
    required: true,
    largura: 6,
    coluna: 1,
    linha: 4,
    visible: ehPernoite,
    opcoes: [
      { label: 'Todas as locações ativas', valor: ALVO_TODOS },
      { label: 'Locações escolhidas', valor: ALVO_POR_LOCACAO },
    ],
  },
  {
    label: 'Locações monitoradas',
    nome: 'locacoesMonitoradas',
    tipo: 'custom',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 5,
    visible: ehPernoite,
    ajudinha: { texto: 'O endereço que vale é o do cadastro do locatário. Se ele ainda não estiver no mapa, você localiza aqui mesmo.' },
    component: (props: any) => React.createElement(LocacoesMonitoramentoPicker, {
      idEmpresa: opts.idEmpresa,
      todas: Number(props.entidade?.alvoTipo) !== ALVO_POR_LOCACAO,
      selecionadasIniciais: opts.locacoesIniciais,
      onChange: opts.onLocacoesChange,
      disabled: opts.modo === TipoFormulario.tpVisualizacao,
    }),
  },
  {
    label: 'Horário da madrugada',
    nome: 'horarioPernoite',
    tipo: 'custom',
    required: false,
    largura: 6,
    coluna: 1,
    linha: 6,
    visible: ehPernoite,
    ajudinha: { texto: 'Usa o fuso do endereço de cada locatário (ex.: no Acre, 2 horas antes de Brasília).' },
    component: (props: any) => React.createElement(HorarioPernoiteInput, {
      entidade: props.entidade,
      handleInputChange: props.handleInputChange,
      disabled: opts.modo === TipoFormulario.tpVisualizacao,
    }),
  },
  {
    label: 'Raio de tolerância (metros)',
    nome: 'raioMetros',
    tipo: 'number',
    required: false,
    largura: 6,
    coluna: 2,
    linha: 6,
    visible: ehPernoite,
    ajudinha: { texto: 'Distância do endereço que ainda conta como "dormiu em casa" (o GPS erra alguns metros e há vaga na rua). De 100 m a 2 km.' },
  },
  {
    label: 'Noites fora para alertar',
    nome: 'noitesFora',
    tipo: 'number',
    required: false,
    largura: 6,
    coluna: 1,
    linha: 7,
    visible: ehPernoite,
  },
  {
    label: 'Nas últimas (noites)',
    nome: 'janelaNoites',
    tipo: 'number',
    required: false,
    largura: 6,
    coluna: 2,
    linha: 7,
    visible: ehPernoite,
    ajudinha: { texto: 'Tamanho da janela que anda: as últimas noites com sinal do rastreador. De 3 a 30.' },
  },
  {
    label: '',
    nome: 'explicacaoPernoite',
    tipo: 'custom',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 7,
    visible: ehPernoite,
    component: (props: any) => React.createElement(ExplicacaoPernoite, { entidade: props.entidade }),
  },
  {
    label: 'Dias de atraso p/ ação',
    nome: 'diasAtrasoParaAcao',
    tipo: 'number',
    required: true,
    largura: 6,
    coluna: 2,
    linha: 4,
    visible: (e: any) => Number(e?.tipo) === TIPO_INADIMPLENCIA,
  },
  {
    label: 'Desbloquear automaticamente ao regularizar o pagamento',
    nome: 'desbloqueioAutomaticoAoPagar',
    tipo: 'checkbox',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 5,
    visible: (e: any) => Number(e?.tipo) === TIPO_INADIMPLENCIA,
    ajudinha: {
      texto: 'Ligado: assim que a parcela em atraso for paga, o veículo é desbloqueado no próximo ciclo (até 15 minutos). Desligado: o veículo continua bloqueado até você desbloquear na aba Ocorrências; a locadora é avisada quando o pagamento entrar.',
      autoCloseTime: 8,
    },
  },
  {
    label: 'Regra de inatividade',
    nome: 'regraInatividade',
    tipo: 'select',
    required: false,
    largura: 6,
    coluna: 2,
    linha: 4,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE,
    opcoes: [
      { label: 'Dias inativos', valor: REGRA_DIAS_INATIVOS },
      { label: 'Km abaixo do esperado', valor: REGRA_KM_BAIXO },
    ],
  },
  {
    label: 'Critério de inatividade',
    nome: 'criterioInatividade',
    tipo: 'select',
    required: false,
    largura: 6,
    coluna: 1,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE && Number(e?.regraInatividade) === REGRA_DIAS_INATIVOS,
    ajudinha: { texto: 'O que conta como dia inativo: veículo que não liga, que não muda de posição, ou as duas coisas juntas.' },
    opcoes: [
      { label: 'Não liga', valor: 1 },
      { label: 'Não muda de posição', valor: 2 },
      { label: 'Ambos (não liga e não muda de posição)', valor: 3 },
    ],
  },
  {
    label: 'Dias inativos p/ alertar',
    nome: 'diasInativos',
    tipo: 'number',
    required: false,
    largura: 6,
    coluna: 2,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE && Number(e?.regraInatividade) === REGRA_DIAS_INATIVOS,
  },
  {
    label: 'Janela (dias)',
    nome: 'janelaDias',
    tipo: 'number',
    required: false,
    largura: 4,
    coluna: 1,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE && Number(e?.regraInatividade) === REGRA_KM_BAIXO,
    ajudinha: { texto: 'Quantos dias para trás somar os km rodados.' },
  },
  {
    label: 'Janela (dias)',
    nome: 'janelaDias',
    tipo: 'number',
    required: false,
    largura: 4,
    coluna: 1,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_EXCESSO_ATIVIDADE,
    ajudinha: { texto: 'Quantos dias para trás somar os km rodados.' },
  },
  {
    label: 'Km mínimo na janela',
    nome: 'kmMinimo',
    tipo: 'number',
    required: false,
    largura: 4,
    coluna: 2,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE && Number(e?.regraInatividade) === REGRA_KM_BAIXO,
    ajudinha: { texto: 'Alerta quando o total rodado na janela ficar abaixo deste valor.' },
  },
  {
    label: 'Km médio por dia',
    nome: 'kmMediaDia',
    tipo: 'custom',
    required: false,
    largura: 4,
    coluna: 3,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE && Number(e?.regraInatividade) === REGRA_KM_BAIXO,
    ajudinha: { texto: 'Preenchendo a média, o km mínimo da janela é calculado sozinho, e o contrário também.' },
    component: (props: any) => React.createElement(KmMediaDiaInput, {
      entidade: props.entidade,
      handleInputChange: props.handleInputChange,
      campoTotal: 'kmMinimo',
    }),
  },
  {
    label: 'Km máximo na janela',
    nome: 'kmMaximo',
    tipo: 'number',
    required: false,
    largura: 4,
    coluna: 2,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_EXCESSO_ATIVIDADE,
    ajudinha: { texto: 'Alerta quando o total rodado na janela passar deste valor.' },
  },
  {
    label: 'Km médio por dia',
    nome: 'kmMediaDia',
    tipo: 'custom',
    required: false,
    largura: 4,
    coluna: 3,
    linha: 6,
    visible: (e: any) => Number(e?.tipo) === TIPO_EXCESSO_ATIVIDADE,
    ajudinha: { texto: 'Preenchendo a média, o km máximo da janela é calculado sozinho, e o contrário também.' },
    component: (props: any) => React.createElement(KmMediaDiaInput, {
      entidade: props.entidade,
      handleInputChange: props.handleInputChange,
      campoTotal: 'kmMaximo',
    }),
  },
  {
    label: 'Cerca (ponto de referência + raio)',
    nome: 'cerca',
    tipo: 'custom',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 4,
    visible: (e: any) => Number(e?.tipo) === TIPO_DISTANCIAMENTO || Number(e?.tipo) === TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO,
    component: (props: any) => React.createElement(MapaCercaPicker, {
      value: props.value,
      onChange: props.onChange,
      modoDuplo: Number(props.entidade?.tipo) === TIPO_ALERTA_BLOQUEIO_DISTANCIAMENTO,
    }),
  },
  {
    label: 'Veículos alvo',
    nome: 'veiculosAlvo',
    tipo: 'multiselect',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 5,
    visible: (e: any) => Number(e?.alvoTipo) === ALVO_POR_VEICULO,
    ajudinha: { texto: 'Selecione os veículos que este evento deve monitorar.' },
    selectMultiProps: {
      modo: opts.modo,
      dataSource: opts.veiculosDataSource,
      initialSelected: opts.veiculosInicial,
      onSelectionChange: opts.onVeiculosChange,
      optionLabelField: 'placa;modelo',
      criticarRepeticao: true,
      optionTag: tagStatusVeiculo,
    },
  },
  {
    label: 'Locatários alvo',
    nome: 'locatariosAlvo',
    tipo: 'multiselect',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 5,
    visible: (e: any) => Number(e?.alvoTipo) === ALVO_POR_LOCATARIO,
    ajudinha: { texto: 'Selecione os locatários que este evento deve monitorar.' },
    selectMultiProps: {
      modo: opts.modo,
      dataSource: opts.locatariosDataSource,
      initialSelected: opts.locatariosInicial,
      onSelectionChange: opts.onLocatariosChange,
      optionLabelField: 'nome',
      criticarRepeticao: true,
    },
  },
  {
    label: '',
    nome: 'explicacaoInatividade',
    tipo: 'custom',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 7,
    visible: (e: any) => Number(e?.tipo) === TIPO_INATIVIDADE || Number(e?.tipo) === TIPO_EXCESSO_ATIVIDADE,
    component: (props: any) => React.createElement(ExplicacaoInatividade, { entidade: props.entidade }),
  },

  // ── Expiração ──────────────────────────────────────────────
  {
    label: 'Expiração',
    nome: 'secExpiracao',
    tipo: 'label',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 8,
    visible: true,
  },
  {
    label: 'Esse evento deverá expirar',
    nome: 'temporario',
    tipo: 'checkbox',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 9,
    visible: true,
    ajudinha: { texto: 'Ao expirar o evento irá parar de ser acionado mesmo que necessário.', autoCloseTime: 4 },
  },
  {
    label: 'Expira em',
    nome: 'dataExpiracao',
    tipo: 'date',
    required: false,
    largura: 6,
    coluna: 1,
    linha: 10,
    visible: (e: any) => Boolean(e?.temporario),
  },

  // ── Alertas ────────────────────────────────────────────────
  // Checkbox, não 'toggle': o toggle do ModalForm desenha um botão por item de `opcoes` e esconde o label.
  // Sem `opcoes`, estes três campos não apareciam na tela: "Alertar locador" ficava sempre desligado e
  // nenhum evento avisava ninguém (só gravava a linha na aba Log).
  {
    label: 'Alertas e situação',
    nome: 'secAlertas',
    tipo: 'label',
    required: false,
    largura: 12,
    coluna: 1,
    linha: 11,
    visible: true,
  },
  {
    label: 'Alertar locador',
    nome: 'alertarLocador',
    tipo: 'checkbox',
    required: false,
    largura: 4,
    coluna: 1,
    linha: 12,
    visible: true,
    ajudinha: { texto: 'Avisa a locadora por notificação no app e por e-mail (o e-mail do cadastro da empresa) quando o evento dispara.', autoCloseTime: 6 },
  },
  {
    label: 'Alertar motorista',
    nome: 'alertarMotorista',
    tipo: 'checkbox',
    required: false,
    largura: 4,
    coluna: 2,
    linha: 12,
    visible: true,
    ajudinha: { texto: 'Avisa o locatário do veículo por notificação no app e por e-mail.', autoCloseTime: 6 },
  },
  {
    label: 'Evento ativo',
    nome: 'ativo',
    tipo: 'checkbox',
    required: false,
    largura: 4,
    coluna: 3,
    linha: 12,
    visible: true,
  },
  {
    label: 'Id',
    nome: 'id',
    tipo: 'text',
    required: false,
    largura: 4,
    coluna: 3,
    linha: 13,
    visible: false,
  },
];
